/* eslint-disable no-magic-numbers */
/* eslint-disable max-lines */
import { createSelector } from 'reselect';
import { urls as itemsUrls } from 'Reducers/items';
import { getFilledCollection, callHasBeenMadeForCollection } from 'Reducers/componentHelpers';
import * as objectsHelper from 'Helpers/objects';
import * as stringsHelper from 'Helpers/strings';
import * as filtersHelper from 'Helpers/filters';
import CookieManager from 'Helpers/cookies';
import { getNestedFilters } from 'Helpers/filtersTanak';
import { itemCollection } from 'Helpers/listing';
import { paramsToQueryString, buildObjectFromURL, queryStringToParams, getItemUrl } from 'Helpers/url';
import { selectedLocationSelector, gpsSelector, isCurrentGPSLocationSelector } from './location';
import { ITEMS_FEED_SOURCE, ITEMS_SEARCH_SOURCE, APIVERSION, INSPECTION_INFO_STATUS, GALLERY_NAVIGATION_TILE_NAME, FCG_TILE_IMG_HEIGHT } from 'Constants/items';
import { ITEM_COUNT } from './../olxautos/constants/itemCarousel';
import { addressComponentsToString } from 'Helpers/locations';
import Auth from '../Auth';
import pickBy from 'lodash/pickBy';
import identity from 'lodash/identity';
import has from 'lodash/has';
import { parseSchemas } from 'Helpers/schemas';
import { findParentCategories } from 'Helpers/categories';
import { mapStateToCategories } from 'Actions/categories';
import { schemasSelector } from './schemas';
import { getType } from 'Reducers/helpers/typeModifier';
import { getSelectedLangCode } from './config';
import { isEmpty } from 'Helpers/objects';
import { getIsStoreInspectedCar, getMappingPriortized, getItemWithLocation, getIsSpinCarEnabled, getGalleryNavTilesOrder } from 'Helpers/item';
import { getImg } from 'Helpers/images';
import { isMobile } from 'Helpers/devices';

export const getUrlType = url => getType('ITEMS', url);

export const isFetching = (state, url) => Boolean(state.items.isFetching[getUrlType(url)]);

export const getItemsUrl = (itemSource, apiVersion = APIVERSION.V1) => itemsUrls.getItems(itemSource, apiVersion);

export const getLangParams = state => {
    const langParams = {
        lang: getSelectedLangCode(state)
    };

    return langParams.lang ? langParams : null;
};

// TODO: check this code
const relevanceCollectionListSelector = createSelector(
    ({ collectionList }) => collectionList,
    ({ collections }) => collections,
    ({ elements }) => elements,
    (collectionList, collections, elements) => {
        const itemIds = collectionList
            .reduce((prev, curr) => {
                const collectionArr = (collections && collections[getUrlType(curr)] || []);

                collectionArr.forEach(val => {
                    if (prev.indexOf(val) === -1 && !Array.isArray(val)) {
                        prev.push(val);
                    }
                });

                return prev;
            }, []);

        return itemIds.map(id => elements[id]);
    }
);

const collectionSimilarListSelector = createSelector(
    ({ collectionList }) => collectionList,
    ({ collections }) => collections,
    ({ similarAdsData }) => similarAdsData,
    (collectionList, collections, elements) => {
        const itemIds = collectionList
            .reduce((prev, curr) => {
                const collectionArr = (collections && collections[getUrlType(curr)] || []);

                collectionArr.forEach(collectionVal => {
                    if (Array.isArray(collectionVal)) {
                        collectionVal.forEach(val => {
                            if (prev.indexOf(val) === -1) {
                                prev.push(val);
                            }
                        });
                    }
                });

                return prev;
            }, []);

        return itemIds.map(id => elements[id]);
    }
);

/**
 * Maps the values in locations_resolved so that we can access them in the format:
 * (COUNTRY, STATE, CITY, NEIGHBOURHOOD) based on the default config.
 * @param {Object} itemElements - current elements available on the store
 * @param {Object} config - config needed to get the mapping
 */
export const locationMappingSelector = createSelector(
    state => state.items.elements,
    state => state.items.similarAdsData,
    (_, props) => props && (props.config || props.marketConfig),
    (itemElements, similarAdsElements, config) => {
        if (!(itemElements || similarAdsElements) || !config) {
            return false;
        }

        const locationMapping = config.get('locationMapping');

        const mappedElementsLocations = (elements, similarAdsElements) => {
            const processedElements = similarAdsElements ? [...Object.keys(elements), ...Object.keys(similarAdsElements)] : Object.keys(elements);

            return processedElements.reduce((previousElm, currentElm) => {
                const elmLocationResolved = elements?.[currentElm]?.locations_resolved || similarAdsElements?.[currentElm]?.locations_resolved;

                if (!objectsHelper.isEmpty(elmLocationResolved) && !elmLocationResolved.prioritizedLevels) {
                    const mappingPrioritized = getMappingPriortized(elmLocationResolved, locationMapping);

                    return {
                        ...previousElm,
                        [currentElm]: mappingPrioritized
                    };
                }

                return previousElm;
            }, {});
        };

        return mappedElementsLocations(itemElements, similarAdsElements);
    }
);

const createItemSelector = (itemsListSelector, itemsMetadataSelector) =>
    createSelector(
        itemsListSelector,
        itemsMetadataSelector,
        locationMappingSelector,
        (itemsList, itemsMetadata, itemsLocations) => {
            let selectedItems;

            if (!itemsLocations || objectsHelper.isEmpty(itemsLocations)) {
                selectedItems = itemsList;
            }
            else {
                selectedItems = itemsList.map(item => {
                    if (item.id in itemsLocations) {
                        return {
                            ...item,
                            locations_resolved: {
                                ...item.locations_resolved,
                                ...itemsLocations[item.id]
                            }
                        };
                    }

                    return item;
                });
            }

            const processedItem = itemCollection(...selectedItems);

            if (itemsMetadata) {
                const { total, cursor, nextPageUrl, nextSuggestedPageUrl, total_suggested_ads } = itemsMetadata;

                processedItem.setTotal(total);
                processedItem.setTotalSuggestedAds(total_suggested_ads);
                processedItem.setCursor(cursor);
                processedItem.setNextPageUrl(nextPageUrl);
                processedItem.setNextSuggestedPageUrl(nextSuggestedPageUrl);
            }

            return processedItem;
        }
    );

export const coverItemsSelector = createItemSelector(
    createSelector(
        ({ items }, { url }) => items.collections[getUrlType(url)],
        ({ items }) => items.elements,
        (collection, elements) =>
            (collection || []).map(id => elements[id])
    ),
    ({ items }, { url }) => items.collectionMetadata[getUrlType(url)]
);

export const suugestedItemsSelector = createItemSelector(
    ({ items }) => relevanceCollectionListSelector(items),
    ({ items }) => items.collectionMetadata
);

export const relevanceItemsSelector = createItemSelector(
    ({ items }) => relevanceCollectionListSelector(items),
    ({ items }) => items.collectionMetadata[getUrlType(items.lastCollectionUrl)]
);

export const carouselItemsSelector = createSelector(
    relevanceItemsSelector,
    items => (items && items.length && items.slice(0, ITEM_COUNT)) || []
);

export const itemsSelector = () => relevanceItemsSelector;

export const itemsUserSelector = (state, userId, params) => {
    if (!userId) {
        return [];
    }

    return getFilledCollection('ITEMS', state.items, itemsUrls.getUserAds(userId), params);
};

export const itemsUserFetching = (state, userId, params) => {
    const itemsUser = itemsUrls.getUserAds(userId);
    const url = params && !objectsHelper.isEmpty(params) ? `${itemsUser}?${paramsToQueryString(params)}` : itemsUser;

    return isFetching(state, url);
};

export const callHasBeenMadeForItemsUser = (state, userId, params) => {
    if (!userId) {
        return false;
    }

    return callHasBeenMadeForCollection(state.items, itemsUrls.getUserAds(userId), params);
};

export const favoriteItemsUserSelector = createSelector(
    ({ items }) => items && items.elements,
    ({ items }) => items && items.collections,
    ({ items }) => items && items.collectionMetadata,
    ({ user }) => user && user.favouritesItems,
    state => state.api && state.api.queryParam && state.api.queryParam.enableMultiLang && state.api.queryParam.lang,
    (elements, collections, metadata, userFavorites, lang) => {
        const user = Auth.getUser();

        if (!user) {
            return [];
        }

        const itemFavAds = itemsUrls.getUserFavAds(user.id);
        const url = lang ? `${itemFavAds}?${paramsToQueryString({ lang })}` : itemFavAds;
        const itemsIds = collections[getUrlType(url)];

        if (!itemsIds) {
            return [];
        }

        const { cursor, nextPageUrl, total, total_suggested_ads } = metadata[getUrlType(url)] || {};
        const favoriteIds = userFavorites && userFavorites.length ? userFavorites : [];
        const favoriteItems = favoriteIds && favoriteIds.reduce((items, item) => {
            if (elements[item?.adId]) {
                items.push(elements[item.adId]);
            }
            return items;
        }, []);

        if (favoriteItems && favoriteItems.length > 0) {
            const items = itemCollection(...favoriteItems);

            items.setTotal(total);
            items.setTotalSuggestedAds(total_suggested_ads);
            items.setCursor(cursor);
            items.setNextPageUrl(nextPageUrl);

            return items;
        }
        return [];
    }
);

export const favoriteItemsUserFetching = createSelector(
    ({ items }) => items && items.isFetching,
    state => state.api && state.api.queryParam && state.api.queryParam.enableMultiLang && state.api.queryParam.lang,
    (isFetching, lang) => {
        const user = Auth.getUser();

        if (!user) {
            return false;
        }
        let url = itemsUrls.getUserFavAds(user.id);

        url = lang ? `${url}?${paramsToQueryString({ lang })}` : url;

        return isFetching[getUrlType(url)];
    }
);

export const callHasBeenMadeForFavoriteItemsUser = (state, userId, params) => {
    if (!userId) {
        return false;
    }

    return callHasBeenMadeForCollection(state.items, itemsUrls.getUserFavAds(userId), params);
};

export const itemsPageIndexSelector = state => {
    const urlParams = (state.items && state.items.lastCollectionUrl)
        ? buildObjectFromURL(state.items.lastCollectionUrl).params : {};

    return urlParams && urlParams.page ? `${urlParams.page}` : '0';
};

export const itemsMetadataSelector = createSelector(
    ({ items }) => items.collectionMetadata,
    ({ items }) => items.lastCollectionUrl,
    (itemsMetadata, collectionKey) => itemsMetadata[getType('ITEMS', collectionKey)]
);

export const itemsSimilarAdsSelector = createSelector(
    ({ items }) => collectionSimilarListSelector(items),
    locationMappingSelector,
    itemsMetadataSelector,
    (similarAds, itemsLocations, itemsMetadata) => {
        let selectedItems;
        const similarAdsArray = similarAds ? Object.values(similarAds) : [];

        if (!itemsLocations || objectsHelper.isEmpty(itemsLocations)) {
            selectedItems = similarAdsArray;
        }
        else {
            selectedItems = similarAdsArray.map(item => {
                if (item.id in itemsLocations) {
                    return {
                        ...item,
                        locations_resolved: {
                            ...item.locations_resolved,
                            ...itemsLocations[item.id]
                        }
                    };
                }

                return item;
            });
        }
        const processedItem = itemCollection(...selectedItems);

        if (itemsMetadata) {
            const { nextPageUrl, nextSuggestedPageUrl } = itemsMetadata;

            processedItem.setNextPageUrl(nextPageUrl);
            processedItem.setNextSuggestedPageUrl(nextSuggestedPageUrl);
        }

        return processedItem;
    }
);

export const itemsFacetMetadataSelector = createSelector(
    ({ items }) => items.collectionMetadata,
    ({ items: { collectionList, lastCollectionUrl } = {}}) => {
        if (Array.isArray(collectionList)) {
            return collectionList[0];
        }
        return lastCollectionUrl;
    },
    (itemsMetadata, collectionKey) => itemsMetadata[getType('ITEMS', collectionKey)]
);

export const isFetchingItemsSelector = () => createSelector(
    ({ items }) => items.isFetching,
    ({ items }) => items.lastCollectionUrl,
    (isFetching, url) => isFetching[getUrlType(url)]
);

export const userSessionSelector = ({ user }) => user && user.sessionLong;

export const categorySelector = ({ categories }, { params }) =>
    categories && categories.elements && params && params.categoryID && categories.elements[params.categoryID];

const getItemsFeedParamsSelector = createSelector(
    (state, props) => selectedLocationSelector(state, props),
    gpsSelector,
    isCurrentGPSLocationSelector,
    (state, { location }) => location && location.query && location.query.page,
    userSessionSelector,
    (locationObject, gps, isGPSLocation, page, user) => {
        const { latitude, longitude } = gps || {};
        const location = isGPSLocation ? undefined : locationObject && locationObject.id;

        return pickBy({
            location,
            latitude: latitude || locationObject.latitude,
            longitude: longitude || locationObject.longitude,
            page,
            user
        }, identity);
    }
);

const getItemsSearchParamsSelector = createSelector(
    (_, { params, text }) => {
        const textParams = params && params.text;

        return text || stringsHelper.decodeSearch(textParams);
    },
    (_, { params }) => params && params.categoryID,
    (_, { params }) => params && params.geoID,
    (state, props) => selectedLocationSelector(state, props),
    (_, { location }) => location && location.query && location.query.filter,
    (_, { location }) => location && location.query && location.query.sorting,
    gpsSelector,
    isCurrentGPSLocationSelector,
    (_, { location }) => location && location.query && location.query.page,
    userSessionSelector,
    (_, { facetLimit }) => facetLimit,
    (state, { params }) => state && state.track && state.track.origin === ITEMS_SEARCH_SOURCE
        && params.isSearchCall,
    ({ filters: { elements } = {}} = {}, { params: { categoryID, apiVersion } = {}} = {}) => {
        if (categoryID && elements && elements[categoryID] && apiVersion === APIVERSION.V4) {
            return elements[categoryID].filtersInfoByAttr || null;
        }
        return null;
    },
    (_, { params }) => params && params.size,
    (_, { params }) => params && params.platform,
    (_, { params }) => params && params.relaxedFilters,
    (...args) => {
        const [
            query,
            category,
            locationByUrl,
            locationState,
            filters,
            sorting,
            gps,
            isGPSLocation,
            page,
            user,
            facetLimit,
            isSearchCall,
            filtersByAttr,
            size,
            platform,
            relaxedFilters
        ] = args;
        const { latitude, longitude } = gps || {};
        const locationId = (!locationByUrl && locationState)
            ? locationState.id
            : locationByUrl;
        const location = isGPSLocation ? undefined : locationId;
        const facetLimitObject = facetLimit
            ? { facet_limit: facetLimit, location_facet_limit: 20 }
            : {};

        let nestedFilters = {};
        const abundanceFilters = filtersHelper.decodeRelevanceFilters(filters);

        if (filtersByAttr) {
            nestedFilters = getNestedFilters(abundanceFilters, filtersByAttr);
        }

        // spelchecker will only work when user searches something
        const spellcheck = !!query;

        const result = {
            query,
            category,
            spellcheck,
            location,
            latitude,
            longitude,
            ...abundanceFilters,
            ...facetLimitObject,
            sorting,
            page,
            user,
            isSearchCall,
            size,
            platform,
            relaxedFilters
        };

        if (!isEmpty(nestedFilters)) {
            result['nested-filters'] = nestedFilters;
        }

        return pickBy(result, identity);
    }
);

export const getItemsParamsSelector = itemsSource => {
    if (itemsSource === ITEMS_FEED_SOURCE) {
        return getItemsFeedParamsSelector;
    }
    return getItemsSearchParamsSelector;
};

export const isFetchingItem = (state, itemId) => {
    const langParams = getLangParams(state);
    const url = itemsUrls.getItem(itemId);
    const updatedUrl = langParams ? `${url}?${paramsToQueryString(langParams)}` : url;

    return state.items.isFetching[getUrlType(updatedUrl)];
};

export const showMakeAnOfferSelector = createSelector(
    ({ items }) => items.elements,
    (_, { params }) => params && params.id,
    (elements, itemId) => {
        if (objectsHelper.isEmpty(elements) || !itemId) {
            return false;
        }

        const item = elements[itemId];

        return !!(item
            && item.price
            && !objectsHelper.isEmpty(item.price)
            && !objectsHelper.isEmpty(item.price.value)
            && item.price.value.raw
            && item.price.value.raw > 0);
    }
);

export const getFeaturedAdSelector = createSelector(
    ({ items, bundles }) => Object.assign({}, items.elements, bundles.elements),
    (_, { itemId }) => itemId,
    (elements, itemId) => {
        if (objectsHelper.isEmpty(elements) || !itemId) {
            return false;
        }

        const item = elements[itemId];

        return !!(
            (
                item.monetizationInfo
                && !objectsHelper.isEmpty(item.monetizationInfo)
                && item.monetizationInfo.currentProduct
            )
            || item.package
        );
    }
);

const doesMetadataSectionExists = metadata => metadata && metadata.sections && metadata.sections.length;

const suggestedMetadataSectionsExist = metadata => metadata && metadata.suggested_sections && metadata.suggested_sections.length;

/**
 * Selects the sections information if available from the items store
 */
export const itemsSectionsSelector = createSelector(
    itemsFacetMetadataSelector,
    itemsFacetsMetadata => {
        if (doesMetadataSectionExists(itemsFacetsMetadata)) {
            return itemsFacetsMetadata.sections;
        }
        return null;
    }
);

export const itemsSearchQuerySelector = createSelector(
    itemsFacetMetadataSelector,
    (_, text) => text,
    (itemsFacetsMetadata, originalQuery) => {
        if (itemsFacetsMetadata && typeof itemsFacetsMetadata.modified_term === 'string' && !itemsFacetsMetadata.removed_term) {
            return itemsFacetsMetadata.modified_term;
        }
        else if (itemsFacetsMetadata && itemsFacetsMetadata.removed_term) {
            return '';
        }
        return originalQuery || '';
    }
);

export const suggestedSectionsSelector = createSelector(
    itemsFacetMetadataSelector,
    itemsMetadata => {
        if (suggestedMetadataSectionsExist(itemsMetadata)) {
            return itemsMetadata.suggested_sections;
        }
        return null;
    }
);

const getRecommendationsUrl = createSelector(
    getSelectedLangCode,
    (_, { item: { id, category_id }, loggedUser, isAdPostedByPremiumUser }, isPreferredAdsFetching) => ({ id, category_id, loggedUser, isAdPostedByPremiumUser, isPreferredAdsFetching }),
    (lang, data) => {
        const userId = (data.loggedUser && data.loggedUser.id) || '';
        const userIdentifier = userId || CookieManager?.readCookie('relevanceUser');
        let url = itemsUrls.recommendations(userIdentifier, data.id, data.category_id);

        if (data.isPreferredAdsFetching) {
            url = data.isAdPostedByPremiumUser
                ? `${itemsUrls.similarAdsFromPremiumUsers(userIdentifier, data.id, data.category_id)}`
                : `${itemsUrls.preferredAdsFromPremiumUsers(userIdentifier, data.id, data.category_id)}`;
        }

        const urlParams = url.split('?')[1];
        const params = urlParams ? queryStringToParams(urlParams) : {};

        params.lang = lang;

        url = url.split('?')[0];
        url = `${url}?${paramsToQueryString(params)}`;
        return url;
    }
);

const recommendationsKeysSelector = createSelector(
    getRecommendationsUrl,
    ({ items }) => items,
    (recommendationUrl, items) => {
        return items && items.collections[getUrlType(recommendationUrl)];
    }
);

export const recommendationsMetaDataSelector = createSelector(
    getRecommendationsUrl,
    ({ items }) => items,
    (recommendationUrl, items) => {
        return items && items.collectionMetadata[getUrlType(recommendationUrl)];
    }
);

export const recommendationsSelector = createSelector(
    ({ items }) => items.elements,
    recommendationsKeysSelector,
    locationMappingSelector,
    (elements, recommendations, itemsLocations) => {
        let selectedItems;
        const arrRecommendations = [];

        if (recommendations && recommendations.length > 0) {
            recommendations.forEach(rec => {
                if (elements[rec]) {
                    arrRecommendations.push(elements[rec]);
                }
            });
        }

        if (!itemsLocations || objectsHelper.isEmpty(itemsLocations)) {
            selectedItems = arrRecommendations;
        }
        else {
            selectedItems = arrRecommendations.map(item => {
                if (item.id in itemsLocations) {
                    return {
                        ...item,
                        locations_resolved: {
                            ...item.locations_resolved,
                            ...itemsLocations[item.id]
                        }
                    };
                }

                return item;
            });
        }

        return selectedItems;
    }
);

export const itemsFirstSectionOffsetSelector = createSelector(
    itemsSectionsSelector,
    sections => {
        if (has(sections, '[0].offset')) {
            return sections[0].offset;
        }

        return null;
    }
);

export const itemsFirstSectionRelaxationTypeSelector = createSelector(
    itemsSectionsSelector,
    sections => {
        if (has(sections, '[0].relaxation_type')) {
            return sections[0].relaxation_type;
        }

        return null;
    }
);

export const suggestedItemsFirstSectionOffsetSelector = createSelector(
    suggestedSectionsSelector,
    sections => {
        if (has(sections, '[0].offset')) {
            return sections[0].offset;
        }
        return null;
    }
);

export const itemSelector = createSelector(
    state => (state.items.elements),
    (_, props) => (props.params.id),
    (items, itemId) => {
        const item = items[itemId];

        if (!item) {
            return null;
        }
        const mappedParams = {};
        const mappedLocation = {};

        if (item.parameters) {
            item.parameters.forEach(param => {
                mappedParams[param.key] = param;
            });
        }
        if (item.metadata && item.metadata.locations && item.metadata.locations[0] && item.metadata.locations[0].tree) {
            item.metadata.locations[0].tree.forEach(location => {
                mappedLocation[location.type.toLowerCase()] = location;
            });
        }
        return { ...item, mappedParams, mappedLocation };
    }
);

export const itemUserSelector = createSelector(
    state => (state.users && state.users.elements),
    itemSelector,
    (userElements, item) => {
        return (item && userElements[item.user_id]) || {};
    }
);

export const getItemLocation = item => {
    const { metadata } = item;
    const locationsObject = metadata
        && metadata.locations
        && metadata.locations.length
        && metadata.locations[0].tree;
    const addressLimit = 3;

    if (locationsObject) {
        return addressComponentsToString(locationsObject, addressLimit);
    }

    return '';
};

export const getItemStructuredData = createSelector(
    itemSelector,
    itemUserSelector,
    mapStateToCategories,
    schemasSelector,
    (item, user, categories, itemSchemas) => {
        if (!item) {
            return null;
        }
        const url = getItemUrl(item.id, item.title);

        return parseSchemas(itemSchemas, { item, user, url, location: getItemLocation(item), parentCategories: findParentCategories(categories, item.category_id) }, 'itemSchema');
    }

);

export const adCountSelector = createSelector(
    relevanceItemsSelector,
    items => {
        const count = {
            inspected_ad_count: 0,
            fa_ad_count: 0
        };

        return items.reduce((count, item) => {
            if (getIsStoreInspectedCar(item.inspection_info)
                && item.inspection_info.consent === INSPECTION_INFO_STATUS.APPROVED) {
                count.inspected_ad_count += 1;
            }
            if (item.package && item.package.id) {
                count.fa_ad_count += 1;
            }
            return count;
        }, count);
    }
);

export const isFavorited = (state, { userId, itemId }) => {
    const { lang } = getLangParams(state) || {};
    const url = itemsUrls.isFavorited(userId, itemId);
    let urlWithLang = '';

    if (lang) {
        urlWithLang = `${url}?lang=${lang}`;
    }

    return state.items.collections[getUrlType(url)]
        || (
            typeof state.items.collections[getUrlType(url)] === 'undefined'
            && state.items.collections[getUrlType(urlWithLang)]
        );
};

export const isFetchingItemDetail = (state, ownProps, apiVersion) => {
    const { lang } = getLangParams(state) || {};

    let url = itemsUrls.getItemDetail(ownProps.params.id, apiVersion);

    if (lang) {
        url += `?lang=${lang}`;
    }

    return state.items.isFetching[getUrlType(url)];
};

const findItemByIdStorage = (itemId, keySessionStorage) => {
    // eslint-disable-next-line no-undef
    const dataFromLocalStorage = JSON.parse(sessionStorage.getItem(keySessionStorage));

    if (Array.isArray(dataFromLocalStorage)) {
        for (const item of dataFromLocalStorage) {
            if (item.id === itemId) {
                return item;
            }
        }
    }

    return {};
};

export const getVasSheetItem = createSelector(
    ({ items }) => items,
    ({ bundles }) => bundles,
    ({ cxeBundles }) => cxeBundles,
    (_, props) => props,
    (items, bundles, cxeBundles, { itemId, config }) => {
        let item = items.elements[itemId] || bundles.elements[itemId] || cxeBundles.elements[itemId] || {};

        if (Object.keys(item).length === 0 && item.constructor === Object) {
            const isMXCL = config.get('olxAutos', 'isMXCL');

            if (isMXCL) {
                item = findItemByIdStorage(itemId, 'recentActivity');
            }
            else {
                item = findItemByIdStorage(itemId, 'recentActivityOlx');
            }
        }
        return getItemWithLocation(item, config);
    }
);

const selectItem = createSelector(
    ({ items }) => items && items.elements,
    (_, { params }) => params && params.id,
    (itemsElements, itemId) => {
        if (!itemsElements || !itemsElements[itemId]) {
            return undefined;
        }

        return itemsElements[itemId];
    }
);

export const selectApdvGalleryImageMapping = createSelector(
    selectItem,
    (_, { isSpinCarEnabled }) => isSpinCarEnabled,
    (selectedItem, isSpinCarEnabled) => {
        if (!selectedItem) {
            return [];
        }
        const { inspectionDetails, images } = selectedItem;
        const { damageReport, otherImages, spinViewUrl } = inspectionDetails || {};
        const { values: damageReportValues = [] } = damageReport || {};
        let imageObj;

        const galleryNavTilesOrder = getGalleryNavTilesOrder(selectedItem);

        return galleryNavTilesOrder.reduce((acc, tileName) => {
            switch (tileName) {
                case GALLERY_NAVIGATION_TILE_NAME.EXTERIOR:
                case GALLERY_NAVIGATION_TILE_NAME.INTERIOR:
                case GALLERY_NAVIGATION_TILE_NAME.DENT_MAP:
                    imageObj = damageReportValues.find(arr => arr.id === tileName);
                    if (imageObj) {
                        acc.push(imageObj);
                    }
                    break;
                case GALLERY_NAVIGATION_TILE_NAME.SELLER:
                    if (images && images.length) {
                        acc.push({ id: 'seller', images });
                    }
                    break;
                case GALLERY_NAVIGATION_TILE_NAME.OTHER:
                    if (otherImages && otherImages.length) {
                        acc.push({ id: 'others', images: otherImages });
                    }
                    break;
                case GALLERY_NAVIGATION_TILE_NAME.SPIN_CAR:
                    if (isSpinCarEnabled && spinViewUrl) {
                        acc.push({ id: GALLERY_NAVIGATION_TILE_NAME.SPIN_CAR, images: [{
                            url: spinViewUrl
                        }] });
                    }
                    break;
            }
            return acc;
        }, []);
    }
);

export const selectAdpvGalleryImages = createSelector(
    selectApdvGalleryImageMapping,
    imageMapping =>
        imageMapping.reduce((acc, { tags, images, id }) => {
            if (id === GALLERY_NAVIGATION_TILE_NAME.SPIN_CAR) {
                return acc;
            }
            if (tags && Array.isArray(tags)) {
                tags.forEach(({ images }) => acc.push(...images));
            }

            if (images && Array.isArray(images)) {
                acc.push(...images);
            }

            return acc;
        }, [])
);

export const selectAdpvGalleryNavigationTilesImages = createSelector(
    selectApdvGalleryImageMapping,
    imageMapping =>
        imageMapping.reduce((acc, { images, tags, name, id }) => {
            const height = isMobile ? FCG_TILE_IMG_HEIGHT.MOBILE : FCG_TILE_IMG_HEIGHT.DESKTOP;

            if (images && Array.isArray(images) && images[0]) {
                const modifiedImage = getImg({ image: images[0], height, width: 1.5 * height, disableSrcSet: true });

                acc.push({
                    id,
                    name,
                    image: modifiedImage
                });
            }
            else if (tags && Array.isArray(tags) && tags[0] && Array.isArray(tags[0].images) && tags[0].images[0]) {
                const modifiedImage = getImg({ image: tags[0].images[0], height, width: 1.5 * height, disableSrcSet: true });

                acc.push({
                    id,
                    name,
                    image: modifiedImage
                });
            }
            return acc;
        }, [])
);

export const adpvFeatureStatusSelector = createSelector(
    itemSelector,
    (_, props) => props && (props.config || props.marketConfig),
    (item, marketConfig) => {
        const showDentMap = marketConfig.get('showDentMap');
        const isSpinCarEnabled = getIsSpinCarEnabled(marketConfig);
        const { inspectionDetails = {}} = item || {};
        const { damageReport = {}, spinViewUrl } = inspectionDetails || {};
        const { values: damageReportValues = [] } = damageReport || {};
        const galleryNavTilesOrder = getGalleryNavTilesOrder(item);
        const status = [];

        if (showDentMap && galleryNavTilesOrder.includes(GALLERY_NAVIGATION_TILE_NAME.DENT_MAP) && damageReportValues.find(arr => arr.id === GALLERY_NAVIGATION_TILE_NAME.DENT_MAP)) {
            status.push(GALLERY_NAVIGATION_TILE_NAME.DENT_MAP);
        }
        if (isSpinCarEnabled && spinViewUrl && galleryNavTilesOrder.includes(GALLERY_NAVIGATION_TILE_NAME.SPIN_CAR)) {
            status.push(GALLERY_NAVIGATION_TILE_NAME.SPIN_CAR);
        }
        return status.join('_');
    }
);
